import { focusManager, QueryCache, QueryClient } from "@tanstack/react-query";

// React Query v5 only listens to `visibilitychange` (tab switch) by default.
// Override to also handle `window focus` (alt-tab / switching app windows).
focusManager.setEventListener((onFocus) => {
	const onVisibilityChange = () => onFocus();
	const onWindowFocus = () => onFocus();
	window.addEventListener("visibilitychange", onVisibilityChange);
	window.addEventListener("focus", onWindowFocus);
	return () => {
		window.removeEventListener("visibilitychange", onVisibilityChange);
		window.removeEventListener("focus", onWindowFocus);
	};
});

import type { AppRouter } from "@apps/server/trpc/routers";
import { createTRPCClient, httpBatchLink, httpSubscriptionLink, retryLink, splitLink } from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import SuperJSON from "superjson";
import { env } from "~/env";
import { logger } from "./logger";

export const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: (error) => {
			logger.error(error, "Query cache error");
		},
	}),
});

export const trpcClient = createTRPCClient<AppRouter>({
	links: [
		splitLink({
			condition: (op) => op.type === "subscription",
			true: [
				retryLink({
					retry: (opts) => {
						const code = opts.error.data?.code;
						if (!code) {
							// This shouldn't happen as our httpSubscriptionLink will automatically retry within when there's a non-parsable response
							logger.error(opts, "No error code found, retrying");
							return true;
						}
						if (code === "UNAUTHORIZED" || code === "FORBIDDEN") {
							logger.error({ code }, "Retrying due to 401/403 error");
							return true;
						}
						return false;
					},
				}),
				httpSubscriptionLink({
					url: `${env.VITE_SERVER_URL}/trpc`,
					transformer: SuperJSON,
					eventSourceOptions: () => ({
						withCredentials: true,
					}),
				}),
			],
			false: httpBatchLink({
				url: `${env.VITE_SERVER_URL}/trpc`,
				transformer: SuperJSON,
				fetch(url, options) {
					return fetch(url, {
						...options,
						credentials: "include",
					});
				},
			}),
		}),
	],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
	client: trpcClient,
	queryClient,
});
